import type { FormState } from './certificate-form'
import { FileText, Upload, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import FilePickerButton from '~/components/FilePickerButton'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { tourAnchor } from '~/helpers/tour/anchors'

type Props = {
  form: FormState
  set: <K extends keyof FormState>(key: K) => (value: FormState[K]) => void
  skeletonActive: boolean
  onLogoFile: (file: File) => void
  onSkeletonFile: (file: File) => void
  onRemoveSkeleton: () => void
}

/** Logo, fond PDF et marges du bloc de texte d'un modèle d'attestation. */
function CertificateLayoutFields({ form, set, skeletonActive, onLogoFile, onSkeletonFile, onRemoveSkeleton }: Props) {
  const { t } = useTranslation()

  return (
    <>
      <div className="flex flex-col gap-1.5" {...tourAnchor('certificates.logo')}>
        <Label>{t('certificates.field.logo')}</Label>
        <p className="text-xs text-muted-foreground">{t('certificates.logo.help')}</p>
        <div className="flex items-center gap-3">
          {form.logo
            ? <img src={form.logo} alt="" className="h-12 w-auto max-w-[160px] rounded border border-border object-contain" />
            : <span className="text-xs text-muted-foreground">{t('certificates.logo.fallback')}</span>}
          <FilePickerButton accept="image/png,image/jpeg" onFile={onLogoFile}>
            <Upload className="mr-2 size-4" />
            {t('certificates.logo.upload')}
          </FilePickerButton>
          {form.logo && (
            <Button type="button" variant="ghost" size="sm" onClick={() => set('logo')('')}>
              <X className="mr-1 size-4" />
              {t('certificates.logo.remove')}
            </Button>
          )}
        </div>
      </div>

      {/* Squelette PDF de fond */}
      <div className="flex flex-col gap-1.5 border-t border-border pt-4" {...tourAnchor('certificates.background')}>
        <Label>{t('certificates.field.skeleton')}</Label>
        <p className="text-xs text-muted-foreground">{t('certificates.skeleton.help')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm">
            <FileText className="size-4 text-muted-foreground" />
            {skeletonActive ? t('certificates.skeleton.active') : t('certificates.skeleton.none')}
          </span>
          <FilePickerButton accept="application/pdf" onFile={onSkeletonFile}>
            <Upload className="mr-2 size-4" />
            {skeletonActive ? t('certificates.skeleton.replace') : t('certificates.skeleton.upload')}
          </FilePickerButton>
          {skeletonActive && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemoveSkeleton}>
              <X className="mr-1 size-4" />
              {t('certificates.skeleton.remove')}
            </Button>
          )}
        </div>
      </div>

      {/* Marges du bloc de texte (utiles surtout avec un squelette) */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('certificates.field.margins')}</Label>
        <p className="text-xs text-muted-foreground">{t('certificates.margins.help')}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(['marginTop', 'marginBottom', 'marginLeft', 'marginRight'] as const).map(key => (
            <div key={key} className="flex flex-col gap-1">
              <label htmlFor={`certificate-${key}`} className="text-xs text-muted-foreground">{t(`certificates.margins.${key}`)}</label>
              <Input
                id={`certificate-${key}`}
                type="number"
                min={0}
                max={300}
                value={form[key]}
                onChange={e => set(key)(Number(e.target.value))}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default CertificateLayoutFields
